import { PanelFrame } from "@/components/panel-frame"
import { SegmentHeader } from "@/components/segment-header"
import { SmallPortrait } from "@/components/small-portrait"
import { useRevealOnScroll } from "@/hooks/use-reveal-on-scroll"
import type { MatchRecap, RecapCommentator } from "@/types"

const TIMING = {
  revealDuration: 0.6, // s — scroll-triggered panel entrance
  revealOffset: 24, // px — translateY start offset before reveal
}

interface BroadcastSummaryProps {
  recap: MatchRecap
}

export { PanelFrame, SmallPortrait }

export function BroadcastSummary({ recap }: BroadcastSummaryProps) {
  const { ref, revealed } = useRevealOnScroll()

  const paragraphs = recap.summary
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <div
      ref={ref}
      className="reveal-on-scroll mx-auto max-w-3xl px-4 pt-16"
      style={{
        opacity: revealed ? 1 : 0,
        transform: revealed
          ? "translateY(0)"
          : `translateY(${TIMING.revealOffset}px)`,
        transition: `opacity ${TIMING.revealDuration}s ease-out, transform ${TIMING.revealDuration}s ease-out`,
      }}
    >
      {/* Card panel with neon frame */}
      <div className="relative">
        <PanelFrame />

        <div
          className="relative overflow-visible pt-10 pb-6"
          style={{
            background: "oklch(0.14 0.008 240)",
            boxShadow:
              "0 4px 24px oklch(0 0 0 / 0.6), inset 0 1px 0 oklch(0.25 0.01 240 / 0.4)",
          }}
        >
          {/* Commentator portraits — overlapping the top edge */}
          <div
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-4"
            style={{ top: -28 }}
          >
            {recap.commentators.map((c: RecapCommentator) => (
              <SmallPortrait
                key={c.key}
                commentatorKey={c.key}
                name={c.name}
                portraitUrl={recap.portrait_urls[`${c.key}_neutral`] ?? null}
              />
            ))}
          </div>

          {/* Section header */}
          <div className="px-8">
            <SegmentHeader
              rules={false}
              className="text-center"
              label="Post-Game Report"
            />

            {/* Neon lower-third divider */}
            <div
              className="mx-auto mt-3 mb-5 h-px max-w-48"
              style={{
                background:
                  "linear-gradient(90deg, transparent, oklch(0.85 0.28 85), transparent)",
              }}
            />

            {/* Summary paragraphs */}
            <div className="space-y-4">
              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-sm leading-relaxed"
                  style={{ color: "oklch(0.75 0.02 80)" }}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
